/**
 * analyticsService.ts -- read-only client for /analytics.
 *
 * Everything is precomputed nightly by nightly-tt-analytics; this hits
 * one endpoint and gets one row per TT account.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export interface StratRow {
  trades: number;
  wins: number;
  losses: number;
  winRate: number | null;
  avgPnl: number | null;
  totalPnl: number;
  avgHoldDays: number | null;
}

export interface ExtremeCard {
  netPnl: number;
  strategy: string;
  underlying: string;
  sellStrike: number;
  buyStrike: number | null;
  optionType: 'C' | 'P' | null;
  expiration: string;
  openedAt: string;
  closedAt: string | null;
  holdDays: number | null;
  context?: {
    open?: { underlyingSpot?: number; vix?: number; ivRank?: number; regime?: any };
    close?: { underlyingSpot?: number; vix?: number; ivRank?: number; regime?: any };
  } | null;
}

export interface AccountAnalytics {
  userId: string;
  accountNumber: string;
  computedAt: string;
  sinceIso: string;
  txnCount: number;
  instanceCount: number;
  txnBreakdown?: {
    total: number;
    equity: number;
    equityOption: number;
    futureOption: number;
    other: number;
  };
  balance?: {
    netLiq: number;
    cashBalance: number;
    derivativeBP: number;    // available BP for options
    equityBP: number;        // available BP for stock
    maintenanceReq: number;  // BP currently used
    openPositionsCount: number;
    snapshotAt: string;
  };
  header: {
    realizedYtd: number;
    realizedAllTime: number;
    unrealized: number;
    ytdNet: number;
    openInstances: number;
    closedInstances: number;
    closedThisYearCount: number;
  };
  cumulativePnlYtd: { date: string; cumulative: number }[];
  byStrategy: Record<string, StratRow>;
  bySymbol: Record<string, StratRow>;
  byDte: Record<string, StratRow>;
  extremes: {
    biggestWin: ExtremeCard | null;
    biggestLoss: ExtremeCard | null;
  };
}

export interface AnalyticsResponse {
  userId: string;
  accounts: AccountAnalytics[];
  accountCount: number;
}

export async function getAnalytics(token: string): Promise<AnalyticsResponse> {
  const res = await fetch(`${API_BASE}/analytics`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`getAnalytics failed: ${res.status}`);
  return res.json();
}
